// Animate only explicit navigation. Ordinary wheel/touch scrolling stays native.
let cancelActiveScroll;

export function cancelPageScroll() {
  cancelActiveScroll?.();
}

export function getCarouselScrollTop({ top, height, cardHeight, headerHeight, viewportHeight }) {
  const edgeSpace = 24;
  // Center the game card below the sticky header, then shift up only as far as
  // needed to keep the complete controls visible with room below them.
  const centeredTop = headerHeight + (viewportHeight - headerHeight - cardHeight) / 2;
  // An oversized card must start below the header, never above the screen.
  // Small screens can scroll naturally to anything that cannot fit at once.
  const viewportTop = Math.max(headerHeight + 12, Math.min(centeredTop, viewportHeight - edgeSpace - height));
  return Math.max(0, top - viewportTop);
}

export function scrollToPagePosition(top) {
  cancelPageScroll();
  const maxTop = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight
  );
  const destination = Math.max(0, Math.min(top, maxTop));
  const start = window.scrollY;
  const distance = destination - start;
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    Math.abs(distance) < 2
  ) {
    window.scrollTo({ top: destination, behavior: 'instant' });
    return Promise.resolve();
  }

  // Larger journeys get more time, with a gentle start and finish.
  const duration = Math.min(1400, 780 + Math.sqrt(Math.abs(distance)) * 15);
  return new Promise((resolve) => {
    let frame;
    let startedAt;
    let finished = false;
    const scrollingKeys = new Set([
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      ' ',
      'Tab'
    ]);
    const stop = () => {
      if (finished) return;
      finished = true;
      window.cancelAnimationFrame(frame);
      for (const event of ['wheel', 'touchstart', 'pointerdown'])
        window.removeEventListener(event, stop);
      window.removeEventListener('keydown', onKeyDown);
      if (cancelActiveScroll === stop) cancelActiveScroll = undefined;
      resolve();
    };
    const onKeyDown = (event) => {
      if (scrollingKeys.has(event.key)) stop();
    };
    const step = (now) => {
      if (finished) return;
      startedAt ??= now;
      const progress = Math.min(1, (now - startedAt) / duration);
      // Quintic smoothstep: continuous velocity and acceleration at both ends.
      const eased = progress ** 3 * (progress * (progress * 6 - 15) + 10);
      window.scrollTo({ top: start + distance * eased, behavior: 'instant' });
      if (progress < 1) frame = window.requestAnimationFrame(step);
      else stop();
    };
    cancelActiveScroll = stop;
    for (const event of ['wheel', 'touchstart', 'pointerdown'])
      window.addEventListener(event, stop, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    frame = window.requestAnimationFrame(step);
  });
}
